/**
 * The introduction is stored as the HTML the document renders, and typed as plain text.
 *
 * <p>
 * The document needs paragraphs - QuestPDF is handed blocks, not a string - so the column
 * stays HTML. What changed is the control: an editor with a toolbar made a one-paragraph
 * opening line feel like a document of its own, and the formatting it offered was never
 * what anybody used. So the text is converted on the way in and on the way out, and
 * neither the API nor the PDF has to know.
 * </p>
 *
 * Anything richer than paragraphs - a bold run, a bullet list - survives as its own text
 * when read back. That is a deliberate flattening rather than an accident: a plain field
 * that silently kept invisible markup would print something the writer could not see.
 */

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  '#39': "'",
  apos: "'",
  nbsp: ' ',
}

/** What somebody typed, recovered from what was stored. */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return ''

  return (
    html
      // Every block that ends becomes a break before the tags are stripped, otherwise
      // three paragraphs come back as one run-on sentence. A paragraph ends with a blank
      // line after it, which is what a writer sees between two of them; a list item and a
      // <br> end with a single one, because that is what those are.
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<\/(p|div|h[1-6]|blockquote)>/gi, '\n\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&(#?\w+);/g, (whole, name: string) => ENTITIES[name.toLowerCase()] ?? whole)
      // A paragraph tag pair produces one newline at its end; a blank line between two
      // paragraphs is what a writer sees, so runs are collapsed to exactly that.
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  )
}

/** What was typed, as the paragraphs the document prints. */
export function plainTextToHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  const lines = escaped
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  return lines.map((line) => `<p>${line}</p>`).join('')
}

/**
 * The tags a narrative may keep. Everything the PDF renderer draws, and nothing else.
 *
 * As wide as the editor's toolbar and it is the same list twice: what survives a paste here
 * is what QuestPDF knows how to draw over there. A tag the editor stored and the document
 * ignored would be formatting somebody could see on screen and never in the customer's
 * hands, which is worse than not offering it. See RichText.ToBlocks in the API - a tag or a
 * declaration added here and not there is exactly that mistake.
 */
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'HR',
  'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'A',

  // Colour and size. Which of the two a browser writes depends on whether it has been
  // told to style with CSS, so both are kept and the renderer reads both.
  'SPAN', 'FONT',
])

/** Tags whose text is not body text, and goes with them. */
const DROPPED_WHOLE = new Set(['SCRIPT', 'STYLE', 'HEAD', 'TITLE', 'IFRAME', 'OBJECT', 'EMBED'])

/** Carries formatting and nothing else. With none left on it, it is markup nobody asked for. */
const ONLY_FOR_ATTRIBUTES = new Set(['SPAN', 'FONT', 'A'])

const COLOUR = /^(#[0-9a-f]{3}|#[0-9a-f]{6}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*[\d.]+\s*)?\))$/
const SIDE = /^(left|center|right|justify|start|end)$/

/**
 * The declarations an inline style may keep, each with the only values it may have.
 *
 * Checked value by value rather than property by property. Allowing `margin` is allowing a
 * box drawn anywhere; what is wanted is the one shape of it a browser writes for "increase
 * indent", and the same goes for the border and padding that come with it.
 */
const ALLOWED_STYLES: Record<string, RegExp> = {
  'text-align': SIDE,
  'margin-left': /^\d+(\.\d+)?px$/,
  margin: /^0(px)?\s+0(px)?\s+0(px)?\s+\d+(\.\d+)?px$/,
  border: /^none$/,
  padding: /^0(px)?$/,
  color: COLOUR,
  // Names only. A length is a size somebody can set to four hundred pixels; a name is a
  // step on a scale, which is what the toolbar offers and what the document draws.
  'font-size': /^(xx-small|x-small|small|medium|large|x-large|xx-large|xxx-large|-webkit-xxx-large)$/,
  'font-weight': /^(normal|bold|bolder|[1-9]00)$/,
  'font-style': /^(normal|italic|oblique)$/,
  'text-decoration': /^((none|underline|line-through)\s*)+$/,
  'text-decoration-line': /^((none|underline|line-through)\s*)+$/,
}

/**
 * The attributes a tag may keep besides `style`, each with the only values it may have.
 *
 * `align` is what Firefox writes for alignment where every other browser writes a style.
 */
const ALLOWED_ATTRIBUTES: Record<string, Record<string, RegExp>> = {
  FONT: { color: /^#[0-9a-f]{3}([0-9a-f]{3})?$/, size: /^[1-7]$/ },
  P: { align: SIDE },
  H1: { align: SIDE },
  H2: { align: SIDE },
  H3: { align: SIDE },
  DIV: { align: SIDE },
}

/** Everything else - javascript:, data:, file: - is a link that does something other than open a page. */
const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:']

/**
 * What the editor may store, given what a browser produced.
 *
 * A contenteditable pastes whatever was on the clipboard - Word's own markup, a website's
 * spans and classes, an inline style per character. Anything outside the allowed list is
 * unwrapped rather than deleted, so the words survive and only the markup goes; attributes
 * are kept only where the document reads them, which takes classes, ids and event handlers
 * with the rest.
 */
export function sanitizeRichText(html: string): string {
  // Parsed into an inert document rather than assigned to a live element. A detached div is
  // still part of this page: an <img onerror> put there by anything other than this editor
  // would be handed a chance to run before a single tag had been removed.
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const host = parsed.body

  const clean = (parent: Element) => {
    for (const node of [...parent.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE) continue

      if (node.nodeType !== Node.ELEMENT_NODE) {
        node.remove()
        continue
      }

      const element = node as Element

      if (DROPPED_WHOLE.has(element.tagName)) {
        element.remove()
        continue
      }

      clean(element)
      scrubAttributes(element)

      // Chrome puts a list inside the paragraph it was started from, and a parser pulls it
      // back out and leaves the paragraph standing empty on either side. Not a blank line
      // somebody typed - that one holds a <br> - just a tag with nothing in it.
      if (element.tagName === 'P' && element.childNodes.length === 0) {
        element.remove()
        continue
      }

      if (ALLOWED_TAGS.has(element.tagName)) {
        // A span that was only there for a class, a link that pointed nowhere it may.
        if (ONLY_FOR_ATTRIBUTES.has(element.tagName) && element.attributes.length === 0) {
          element.replaceWith(...element.childNodes)
        }

        continue
      }

      if (element.tagName === 'DIV') {
        // A <div> is a line, not decoration. Chrome ends a line with one unless it is told
        // otherwise, and unwrapping it would run two typed lines into a single sentence -
        // which is the whole paragraph break the writer just made, silently lost. It becomes
        // the paragraph it stands for, and keeps whatever alignment or indent it carried.
        if (!element.querySelector('p, ul, ol, div, h1, h2, h3, blockquote, hr')) {
          const paragraph = element.ownerDocument.createElement('p')

          for (const attribute of [...element.attributes]) {
            paragraph.setAttribute(attribute.name, attribute.value)
          }

          paragraph.append(...element.childNodes)
          element.replaceWith(paragraph)
          continue
        }

        // Wrapping blocks that already are blocks, and saying something about all of them:
        // this is how Firefox writes an indent. Unwrapped, the indent goes with it.
        if (element.attributes.length > 0) continue
      }

      // Unwrapped, not deleted: a <span> around a sentence is markup nobody asked for, and
      // the sentence inside it is what the rep wrote.
      element.replaceWith(...element.childNodes)
    }
  }

  clean(host)
  return host.innerHTML
}

function scrubAttributes(element: Element) {
  const allowed = ALLOWED_ATTRIBUTES[element.tagName] ?? {}

  for (const attribute of [...element.attributes]) {
    const name = attribute.name.toLowerCase()
    const value = attribute.value.trim()

    if (name === 'style') {
      const style = scrubStyle(value)

      if (style) element.setAttribute('style', style)
      else element.removeAttribute('style')

      continue
    }

    if (name === 'href' && element.tagName === 'A' && isLinkAddress(value)) continue

    // Takes every on* handler with it, whatever the browser called it.
    if (!allowed[name]?.test(value.toLowerCase())) {
      element.removeAttribute(attribute.name)
    }
  }

  if (element.tagName === 'A' && element.hasAttribute('href')) {
    // A read-only quotation is an ordinary page, and a link in it is followed. In a new
    // tab, so reading the remarks never navigates somebody out of the quotation, and
    // without a handle back to the page it was opened from.
    element.setAttribute('target', '_blank')
    element.setAttribute('rel', 'noopener noreferrer')
  }
}

function scrubStyle(value: string): string {
  return value
    .split(';')
    .map((declaration) => {
      const colon = declaration.indexOf(':')
      if (colon <= 0) return ''

      const property = declaration.slice(0, colon).trim().toLowerCase()
      const setting = declaration.slice(colon + 1).trim().toLowerCase()

      return ALLOWED_STYLES[property]?.test(setting) ? `${property}: ${setting}` : ''
    })
    .filter((declaration) => declaration.length > 0)
    .join('; ')
}

/**
 * Whether an address is one the document will make a link of.
 *
 * Absolute, and one of three schemes - the same test the PDF renderer applies. A relative
 * address means nothing inside a PDF, so it is not kept here to look like a link on screen
 * and print as plain words.
 */
function isLinkAddress(href: string): boolean {
  try {
    return ALLOWED_SCHEMES.includes(new URL(href).protocol)
  } catch {
    return false
  }
}

/**
 * Stored text as the editor should load it.
 *
 * The narrative column has always held HTML, and for a while the catalogue form wrote plain
 * text straight into it. Those rows are still there, and a plain paragraph loaded into an
 * editor as-is loses every line break it had. Anything without a tag is treated as what it
 * is - typing - and given its paragraphs back.
 */
export function asRichHtml(stored: string | null | undefined): string {
  if (!stored) return ''

  return /<[a-z][\s\S]*>/i.test(stored) ? stored : plainTextToHtml(stored)
}

/** Whether anything was actually written, ignoring the markup around it. */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  return htmlToPlainText(html).length === 0
}
